import client from './client';
import { fetchAllPages } from './fetchAllPages';
import { applyApprovedOnlyParams, applyScopedParams } from './viewerAccess';
import { publishCollectionChange } from '../utils/collectionSync';
import { extractCollectionResponse } from './collectionResponse';
import { withOfflineCollectionSupport } from './offlineCollectionSupport';
import { normalizeSeasonalFlagValue } from '../component/MarketTrad/marketTradOptions';

function trimOrNull(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
}

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isNaN(parsed) ? null : parsed;
}

function cleanQueryValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  const text = String(value).trim();
  return text ? text : null;
}

function buildQueryParams(params = {}, defaults = {}) {
  const query = { ...defaults };

  Object.entries(applyScopedParams(applyApprovedOnlyParams(params))).forEach(([key, value]) => {
    const cleaned = cleanQueryValue(value);
    if (cleaned === null || cleaned === undefined) return;
    query[key] = cleaned;
  });

  return query;
}

function unwrapResponse(responseData) {
  return responseData?.data ?? responseData ?? {};
}

function extractCollection(responseData, normalize) {
  return extractCollectionResponse(responseData, normalize);
}

function extractItem(responseData, normalize, targetUuid) {
  const payload = unwrapResponse(responseData);
  const candidates = [payload, payload?.data];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (Array.isArray(candidate.items)) {
      const match = targetUuid
        ? candidate.items.find((item) => item?.uuid === targetUuid)
        : candidate.items[0];

      if (match) return normalize(match);
    }

    if (candidate.uuid || candidate.price_ngn || candidate.priceNgn) {
      return normalize(candidate);
    }
  }

  return normalize({});
}

function makeCollectionApi({ basePath, normalize, buildPayload, defaultPerPage = 25, collectionKey }) {
  const syncKey = collectionKey ?? basePath.replace(/^\/v1\//, '').replace(/^\//, '').replace(/\/$/, '');

  async function fetchPage(params = {}) {
    const { data } = await client.get(basePath, {
      params: buildQueryParams(params, {
        per_page: params.per_page ?? defaultPerPage,
        page: params.page ?? 1,
      }),
    });
    return extractCollection(data, normalize);
  }

  return {
    collectionKey: syncKey,

    async list(params = {}) {
      return fetchPage(params);
    },

    async listAll(params = {}) {
      return fetchAllPages((page) => fetchPage({ ...params, page }));
    },

    async get(uuid) {
      const { data } = await client.get(`${basePath}/${uuid}`);
      return extractItem(data, normalize, uuid);
    },

    async create(payload) {
      const { data } = await client.post(basePath, buildPayload(payload));
      const item = extractItem(data, normalize);
      publishCollectionChange(syncKey, { action: 'create', uuid: item?.uuid ?? null });
      return item;
    },

    async update(uuid, payload) {
      const { data } = await client.put(`${basePath}/${uuid}`, buildPayload(payload));
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(syncKey, { action: 'update', uuid });
      return item;
    },

    async remove(uuid) {
      const { data } = await client.delete(`${basePath}/${uuid}`);
      publishCollectionChange(syncKey, { action: 'remove', uuid });
      return data;
    },
  };
}

function normalizeMarketPrice(row = {}) {
  const marketObj = row.market && typeof row.market === 'object' ? row.market : {};
  const speciesObj = row.species && typeof row.species === 'object' ? row.species : {};

  const marketUuid = row.market_uuid ?? row.market_id ?? row.marketId ?? marketObj.uuid ?? null;
  const speciesUuid = row.species_uuid ?? speciesObj.uuid ?? null;
  const seasonalFlag = normalizeSeasonalFlagValue(row.seasonal_flag ?? row.seasonalFlag ?? '');
  const gradeQuality = row.grade_quality ?? row.gradeQuality ?? '';
  const volumeTraded = toNumberOrNull(row.volume_traded ?? row.volumeTraded);
  const dataStatus = row.data_status ?? row.validation_status ?? row.workflow_status ?? 'draft';

  const marketName =
    row.market_name ??
    row.marketName ??
    marketObj.market_name ??
    marketObj.marketName ??
    marketObj.name ??
    '';

  const speciesCode =
    row.species_code ??
    row.speciesCode ??
    speciesObj.species_code ??
    speciesObj.speciesCode ??
    '';

  const speciesName =
    row.species_name ??
    row.speciesName ??
    speciesObj.common_name_nigeria ??
    speciesObj.commonNameNigeria ??
    speciesObj.name ??
    speciesCode ??
    '';

  return {
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    market_uuid: marketUuid,
    marketUuid,
    market_name: marketName,
    marketName,
    market: marketObj?.uuid || marketObj?.name ? marketObj : null,
    state: row.state ?? marketObj.state ?? '',
    lga: row.lga ?? marketObj.lga ?? '',
    species_uuid: speciesUuid,
    speciesUuid,
    species_code: speciesCode,
    speciesCode,
    species_name: speciesName,
    speciesName,
    price_ngn: toNumberOrNull(row.price_ngn ?? row.priceNgn),
    priceNgn: toNumberOrNull(row.price_ngn ?? row.priceNgn),
    price_date: row.price_date ?? row.priceDate ?? '',
    priceDate: row.price_date ?? row.priceDate ?? '',
    market_id: marketUuid,
    marketId: marketUuid,
    data_source: row.data_source ?? row.dataSource ?? '',
    dataSource: row.data_source ?? row.dataSource ?? '',
    commodity_form: row.commodity_form ?? row.commodityForm ?? '',
    commodityForm: row.commodity_form ?? row.commodityForm ?? '',
    price_unit: row.price_unit ?? row.priceUnit ?? '',
    priceUnit: row.price_unit ?? row.priceUnit ?? '',
    price_type: row.price_type ?? row.priceType ?? '',
    priceType: row.price_type ?? row.priceType ?? '',
    seasonal_flag: seasonalFlag,
    seasonalFlag,
    grade_quality: gradeQuality,
    gradeQuality,
    volume_traded: volumeTraded,
    volumeTraded,
    data_status: dataStatus || 'draft',
    dataStatus,
    notes: row.notes ?? '',
    created_at: row.created_at ?? row.createdAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    raw: row,
  };
}

function buildMarketPricePayload(payload = {}) {
  const source = payload.raw ?? payload.existing ?? payload.original ?? {};
  const marketId = trimOrNull(
    payload.market_uuid ??
    payload.market_id ??
    payload.marketUuid ??
    payload.marketId ??
    source.market_uuid ??
    source.market_id ??
    source.marketUuid ??
    source.marketId
  );

  return {
    market_uuid: marketId,
    market_id: marketId,
    species_uuid: trimOrNull(
      payload.species_uuid ??
      payload.speciesUuid ??
      source.species_uuid ??
      source.speciesUuid
    ),
    price_ngn: toNumberOrNull(payload.price_ngn ?? payload.priceNgn ?? source.price_ngn ?? source.priceNgn),
    price_date: trimOrNull(payload.price_date ?? payload.priceDate ?? source.price_date ?? source.priceDate),
    data_source: trimOrNull(payload.data_source ?? payload.dataSource ?? source.data_source ?? source.dataSource),
    commodity_form: trimOrNull(payload.commodity_form ?? payload.commodityForm ?? source.commodity_form ?? source.commodityForm),
    price_unit: trimOrNull(payload.price_unit ?? payload.priceUnit ?? source.price_unit ?? source.priceUnit),
    price_type: trimOrNull(payload.price_type ?? payload.priceType ?? source.price_type ?? source.priceType),
    seasonal_flag: trimOrNull(
      payload.seasonal_flag ??
      payload.seasonalFlag ??
      source.seasonal_flag ??
      source.seasonalFlag
    ),
    grade_quality: trimOrNull(payload.grade_quality ?? payload.gradeQuality ?? source.grade_quality ?? source.gradeQuality),
    volume_traded: toNumberOrNull(payload.volume_traded ?? payload.volumeTraded ?? source.volume_traded ?? source.volumeTraded),
    data_status: trimOrNull(
      payload.data_status ??
      payload.dataStatus ??
      source.data_status ??
      source.dataStatus ??
      source.validation_status ??
      source.workflow_status ??
      'draft'
    ) ?? 'draft',
    notes: trimOrNull(payload.notes ?? source.notes),
    ...(payload.rejection_notes !== undefined
      ? { rejection_notes: trimOrNull(payload.rejection_notes) }
      : {}),
  };
}

const marketPricesBaseApi = makeCollectionApi({
  basePath: '/v1/market-prices',
  normalize: normalizeMarketPrice,
  buildPayload: buildMarketPricePayload,
  defaultPerPage: 25,
  collectionKey: 'market-prices',
});

export const marketPricesApi = withOfflineCollectionSupport(marketPricesBaseApi, {
  basePath: '/v1/market-prices',
  collectionKey: 'market-prices',
  normalize: normalizeMarketPrice,
  buildPayload: buildMarketPricePayload,
});

marketPricesApi.submit = async function submit(uuid) {
  const { data } = await client.post(`/v1/market-prices/${uuid}/submit`);
  const item = extractItem(data, normalizeMarketPrice, uuid);
  publishCollectionChange(marketPricesBaseApi.collectionKey, { action: 'submit', uuid });
  return item;
};

marketPricesApi.validate = async function validate(uuid) {
  const { data } = await client.post(`/v1/market-prices/${uuid}/validate`);
  const item = extractItem(data, normalizeMarketPrice, uuid);
  publishCollectionChange(marketPricesBaseApi.collectionKey, { action: 'validate', uuid });
  return item;
};

// Approve / Reject have their own backend endpoints (routes/api.php
// validationTransitions) — the generic update() only accepts Draft/Rejected
// records, so it cannot move a Validated record on.
marketPricesApi.approve = async function approve(uuid) {
  const { data } = await client.post(`/v1/market-prices/${uuid}/approve`);
  const item = extractItem(data, normalizeMarketPrice, uuid);
  publishCollectionChange(marketPricesBaseApi.collectionKey, { action: 'approve', uuid });
  return item;
};

marketPricesApi.reject = async function reject(uuid, notes = '') {
  if (!notes || !notes.trim()) throw new Error('Rejection reason is required.');
  const { data } = await client.post(`/v1/market-prices/${uuid}/reject`, { notes: notes.trim() });
  const item = extractItem(data, normalizeMarketPrice, uuid);
  publishCollectionChange(marketPricesBaseApi.collectionKey, { action: 'reject', uuid });
  return item;
};

  // Returns SUM(price_ngn × volume_traded) for approved records (₦ value).
  // Tries a /summary endpoint first; falls back to a `totals` block in the
  // list response. Returns 0 when neither source provides a finite value —
  // the card then displays "₦0 Trade Value" as instructed.
marketPricesApi.tradeValueTotal = async function tradeValueTotal() {
    // 1. Dedicated summary / aggregate endpoint
    try {
      const { data } = await client.get('/v1/market-prices/summary', {
        params: applyScopedParams(applyApprovedOnlyParams({})),
      });
      const payload = data?.data ?? data ?? {};
      const n = Number(
        payload.trade_value        ??
        payload.total_trade_value  ??
        payload.value_total        ??
        payload.total
      );
      if (Number.isFinite(n) && n > 0) return n;
    } catch { /* endpoint may not exist yet */ }

    // 2. Aggregates block inside a normal list response
    try {
      const { items } = await marketPricesApi.listAll({ per_page: 1000 });
      const total = items.reduce((sum, row) => {
        const price = Number(String(row.price_ngn ?? row.priceNgn ?? 0).replace(/,/g, '')) || 0;
        const volume = Number(String(row.volume_traded ?? row.volumeTraded ?? 0).replace(/,/g, '')) || 0;
        return sum + (price * volume);
      }, 0);
      if (Number.isFinite(total) && total > 0) return total;
    } catch { /* ignore */ }

    return 0;
};

function normalizeSummaryMarket(row = {}) {
  return {
    uuid: row.uuid ?? null,
    name: row.market_name ?? row.marketName ?? '',
    state: row.state ?? '',
    price: toNumberOrNull(row.price) ?? 0,
  };
}

function normalizeSummaryCommodity(row = {}) {
  const speciesObj = row.species ?? {};
  return {
    speciesUuid: speciesObj.uuid ?? null,
    speciesCode: speciesObj.code ?? '',
    speciesName: speciesObj.common_name_nigeria ?? speciesObj.code ?? '',
    commodityForm: row.commodity_form ?? '',
    priceUnit: row.price_unit ?? '',
    currentPrice: toNumberOrNull(row.current_price),
    previousPrice: toNumberOrNull(row.previous_price),
    change: toNumberOrNull(row.change),
    changePercent: toNumberOrNull(row.change_percent),
    lowestPrice: toNumberOrNull(row.lowest_price),
    highestPrice: toNumberOrNull(row.highest_price),
    seasonalFlag: normalizeSeasonalFlagValue(row.seasonal_flag ?? row.seasonalFlag ?? ''),
    dataSource: row.data_source ?? '',
    sampleSize: toNumberOrNull(row.sample_size) ?? 0,
    marketsReporting: toNumberOrNull(row.markets_reporting) ?? 0,
  };
}

function normalizeSummary(payload = {}) {
  const scope = payload.scope ?? {};
  const commodities = Array.isArray(payload.commodities) ? payload.commodities : [];
  const marketsReporting = payload.markets_reporting ?? {};

  return {
    scope: {
      state: scope.state ?? null,
      lga: scope.lga ?? null,
      commodityForm: scope.commodity_form ?? null,
      priceType: scope.price_type ?? null,
      windowDays: scope.window_days ?? null,
      currentPeriod: scope.current_period ?? null,
      previousPeriod: scope.previous_period ?? null,
    },
    commodities: commodities.map(normalizeSummaryCommodity),
    marketsReporting: {
      total: toNumberOrNull(marketsReporting.total) ?? 0,
      highest: (marketsReporting.highest ?? []).map(normalizeSummaryMarket),
      lowest: (marketsReporting.lowest ?? []).map(normalizeSummaryMarket),
    },
  };
}

function cleanSummaryParams(params = {}) {
  const query = {};
  Object.entries(params).forEach(([key, value]) => {
    const cleaned = cleanQueryValue(value);
    if (cleaned === null || cleaned === undefined) return;
    query[key] = cleaned;
  });
  return query;
}

// GET /v1/market-prices/summary — aggregated per-commodity pricing + top/bottom
// reporting markets. Accepts: state, lga, commodity_form, price_type,
// species_code, seasonal_flag, window_days, top, to.
marketPricesApi.summary = async function summary(params = {}) {
  const { data } = await client.get('/v1/market-prices/summary', {
    params: applyScopedParams(applyApprovedOnlyParams(cleanSummaryParams(params))),
  });
  return normalizeSummary(data?.data ?? data ?? {});
};

export { normalizeMarketPrice, buildMarketPricePayload };
